import React, { FC, memo } from 'react';
import TableSimple from 'src/components/TableSimple';
import { CustomRenderVariantType, TableColumnTypeVariant } from 'src/types/table';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import Select from 'src/components/Select';
import { SkladonMappingType } from '../../types/skladonMapping';
import { OrderType } from '../../types/order';
import { translateOptions } from '../../resources/constantsCodes/functions';
import translate from '@translate';
import { Box } from '@mui/material';

export type MappingPaymentType =
    { ID: number, ID_PAYMENT_METHOD?: number, ID_PAID?: number, PAID: string, SAVED?: string }
    & Pick<SkladonMappingType, 'UID' | 'NAME' | 'IMPORT'>
    & Pick<OrderType, 'PAYMENT_METHOD'>;
export type MappingPaymentsEditKeysType = keyof MappingPaymentType;
const allColumns: TableColumnTypeVariant<MappingPaymentType>[] = [
    {
        key: 'UID',
        label: 'uid',
        customRender: true
    },
    {
        key: 'NAME',
        label: 'name'
    },
    {
        key: 'PAYMENT_METHOD',
        label: 'paymentMethod',
        customRender: true
    },
    {
        key: 'PAID',
        label: 'paid',
        customRender: true
    },
    {
        key: 'IMPORT',
        label: 'import',
        type: 'select',
        selectOption: 'boolNumberCodes',
        customRender: true
    },
    {
        key: 'SAVED',
        label: 'saved',
        customRender: true
    }
];

export const paidMappingCodes = [
    { label: 'utils.paidMappingCodes.noMapping', value: 'noMapping' },
    { label: 'utils.paidMappingCodes.yes', value: '1' },
    { label: 'utils.paidMappingCodes.no', value: '0' }
];


export interface PaymentMethodsTablePropsType {
    data?: SkladonMappingType[] | null;
    boolNumberCodes: ConstantsCodeType[];
    paymentMethodCodes: ConstantsCodeType[];
    changeValue: (key: MappingPaymentsEditKeysType, value: any, error: boolean, id: number) => void;
    changeValues: (changes: { key: MappingPaymentsEditKeysType, value: any, error: boolean }[], id: number) => void;
    loading: boolean;
}

const PaymentMethodsTable: FC<PaymentMethodsTablePropsType> = (props) => {

    const getInputName = (id: number, key: string) => `product-${id}-${key}`;
    const customRender: CustomRenderVariantType<MappingPaymentType> = (column, row) => {
        const name = getInputName(row.ID, column.key);
        let serviceCodesFiltered: ConstantsCodeType[] = [];
        switch (column.key) {
            case 'PAYMENT_METHOD':
                return (
                    <Select
                        options={props.paymentMethodCodes}
                        value={row.PAYMENT_METHOD}
                        onChange={(value, error) => {
                            props.changeValue('PAYMENT_METHOD', value, error, row.ID);
                        }}
                        name={name}
                        fullWidth
                    />
                );
            case 'PAID':
                return (
                    <Select
                        options={translateOptions(paidMappingCodes)}
                        value={row.PAID ?? 'noMapping'}
                        onChange={(value, error) => props.changeValue('PAID', value, error, row.ID)}
                        name={name}
                        fullWidth
                    />
                );
            case 'IMPORT':
                return (
                    <Select
                        options={props.boolNumberCodes}
                        value={row.IMPORT ?? 1}
                        onChange={(value, error) => props.changeValue('IMPORT', value, error, row.ID)}
                        name={name}
                        fullWidth
                    />
                );
            case 'SAVED':
                const isSaved = (row.ID_PAYMENT_METHOD && row.ID_PAYMENT_METHOD > 0) || (row.ID_PAID && row.ID_PAID > 0);
                return (
                    <Box
                        sx={{
                            color: isSaved ? 'green' : 'red',
                            fontWeight: 'bold'
                        }}
                    >
                        {isSaved
                            ? translate('integration.mappingTable.saved.yes')
                            : translate('integration.mappingTable.saved.notSaved')
                        }
                    </Box>
                );
            default:
                return null;
        }
    };

    const getRowSx = (row: any) => {
        const isSaved = (row.ID_PAYMENT_METHOD && row.ID_PAYMENT_METHOD > 0) || (row.ID_PAID && row.ID_PAID > 0);
        if (!isSaved) {
            return {
                backgroundColor: '#ffebee' // Light red background for unsaved rows
            };
        }
        return {};
    };

    return (
        <Box data-testid="integration-payment-methods">
            <TableSimple
                rowIdKey="ID"
                rows={props.data || []}
                columsLabelPrefix="integration.paymentMethodsTable"
                allColumns={allColumns}
                initSort={{ column: 'UID', direction: 'ASC', createIdNumber: true }}
                selectOptions={{
                    boolNumberCodes: props.boolNumberCodes
                }}
                customRender={customRender}
                rowSxFce={getRowSx}
                disableFilter
                maxHeight={'unset'}
                defaultItems={100}
                loading={props.loading}
            />
        </Box>
    );
};

export default memo(PaymentMethodsTable);
